import { Icon } from "@iconify/react/dist/iconify.js";
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { api } from "../services/api";

const AssignRoleLayer = () => {
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // For pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [usersPerPage, setUsersPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterStatus, setFilterStatus] = useState("All");

  // Fetch users from API
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const data = await api.users.getAll();
      setUsers(data.data);
      setLoading(false);
    } catch (err) {
      setError("Failed to fetch users. Please try again.");
      setLoading(false);
      console.error("Error fetching users:", err);
    }
  };

  // Fetch roles from API
  const fetchRoles = async () => {
    try {
      const data = await api.roles.getAll();

      // Format the role names for display
      const formattedRoles = data.data.map((role) => ({
        id: role.name,
        name: formatRoleName(role.name),
      }));

      setRoles(formattedRoles);
    } catch (err) {
      setError("Failed to fetch roles. Please try again.");
      console.error("Error fetching roles:", err);
    }
  };

  // Format role name for display (convert USER to User, GAME_DEVELOPER to Game Developer)
  const formatRoleName = (roleName) => {
    return roleName
      .split("_")
      .map((word) => word.charAt(0) + word.slice(1).toLowerCase())
      .join(" ");
  };

  useEffect(() => {
    fetchUsers();
    fetchRoles();
  }, []);

  // Assign role to user
  const assignRole = async (userId, role) => {
    try {
      await api.roles.assignRole(userId, role);

      // Refresh user list to show the updated role
      fetchUsers();
    } catch (err) {
      setError(err.message || "Failed to assign role. Please try again.");
      console.error("Error assigning role:", err);
    }
  };

  // Filter users based on search and status
  const filteredUsers = users.filter((user) => {
    const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
    const matchesSearch =
      fullName.includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    // Check if user has a particular role based on filterStatus
    if (filterStatus === "All") return matchesSearch;
    if (filterStatus === "USER") return matchesSearch && user.role === "USER";
    if (filterStatus === "ADMIN") return matchesSearch && user.role === "ADMIN";
    if (filterStatus === "INSTRUCTOR")
      return matchesSearch && user.role === "INSTRUCTOR";

    return matchesSearch;
  });

  // Pagination
  const indexOfLastUser = currentPage * usersPerPage;
  const indexOfFirstUser = indexOfLastUser - usersPerPage;
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser);
  const totalPages = Math.ceil(filteredUsers.length / usersPerPage);

  // Handle page change
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  return (
    <div className="card h-100 p-0 radius-12">
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={usersPerPage}
            onChange={(e) => setUsersPerPage(parseInt(e.target.value))}
          >
            <option value="5">5</option>
            <option value="10">10</option>
            <option value="15">15</option>
            <option value="20">20</option>
          </select>
          <form className="navbar-search" onSubmit={(e) => e.preventDefault()}>
            <input
              type="text"
              className="bg-base h-40-px w-auto"
              name="search"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
            <Icon icon="ion:search-outline" className="icon" />
          </form>
          <select
            className="form-select form-select-sm w-auto ps-12 py-6 radius-12 h-40-px"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="All">All Users</option>
            <option value="USER">Regular Users</option>
            <option value="ADMIN">Admins</option>
            <option value="INSTRUCTOR">Instructors</option>
          </select>
        </div>
      </div>
      <div className="card-body p-24">
        {error && <div className="alert alert-danger">{error}</div>}
        {loading ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : (
          <div className="table-responsive scroll-sm">
            <table className="table bordered-table sm-table mb-0">
              <thead>
                <tr>
                  <th scope="col">
                    <div className="d-flex align-items-center gap-10">
                      <div className="form-check style-check d-flex align-items-center">
                        <input
                          className="form-check-input radius-4 border input-form-dark"
                          type="checkbox"
                          name="checkbox"
                          id="selectAll"
                        />
                      </div>
                      S.L
                    </div>
                  </th>
                  <th scope="col">Username</th>
                  <th scope="col" className="text-center">
                    Current Role
                  </th>
                  <th scope="col" className="text-center">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentUsers.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center">
                      No users found
                    </td>
                  </tr>
                ) : (
                  currentUsers.map((user, index) => (
                    <tr key={user.id}>
                      <td>
                        <div className="d-flex align-items-center gap-10">
                          <div className="form-check style-check d-flex align-items-center">
                            <input
                              className="form-check-input radius-4 border border-neutral-400"
                              type="checkbox"
                              name="checkbox"
                            />
                          </div>
                          {indexOfFirstUser + index + 1}
                        </div>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {user.avatar ? (
                            <img
                              src={user.avatar}
                              alt=""
                              className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden"
                            />
                          ) : (
                            <div className="w-40-px h-40-px rounded-circle flex-shrink-0 me-12 overflow-hidden bg-primary-600 text-white d-flex justify-content-center align-items-center">
                              {(user.firstName || user.lastName || "")
                                .charAt(0)
                                .toUpperCase()}
                            </div>
                          )}
                          <div className="flex-grow-1">
                            <span className="text-md mb-0 fw-normal text-secondary-light">
                              {user.firstName} {user.lastName}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="text-center">
                        {formatRoleName(user.role)}
                      </td>
                      <td className="text-center">
                        <div className="dropdown">
                          <button
                            className="btn btn-outline-primary-600 not-active px-18 py-11 dropdown-toggle toggle-icon"
                            type="button"
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                          >
                            Assign Role
                          </button>
                          <ul className="dropdown-menu">
                            {roles.map((role) => (
                              <li key={role.id}>
                                <Link
                                  className="dropdown-item px-16 py-8 rounded text-secondary-light bg-hover-neutral-200 text-hover-neutral-900"
                                  to="#"
                                  onClick={() => assignRole(user.id, role.id)}
                                >
                                  {role.name}
                                </Link>
                              </li>
                            ))}
                          </ul>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {indexOfFirstUser + 1} to{" "}
            {Math.min(indexOfLastUser, filteredUsers.length)} of{" "}
            {filteredUsers.length} entries
          </span>
          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                to="#"
                onClick={() => paginate(currentPage - 1)}
              >
                <Icon icon="ep:d-arrow-left" className="" />
              </Link>
            </li>
            {[...Array(totalPages).keys()].map((number) => (
              <li key={number + 1} className="page-item">
                <Link
                  className={`page-link fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === number + 1
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200 text-secondary-light"
                  }`}
                  to="#"
                  onClick={() => paginate(number + 1)}
                >
                  {number + 1}
                </Link>
              </li>
            ))}
            <li
              className={`page-item ${
                currentPage === totalPages ? "disabled" : ""
              }`}
            >
              <Link
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md"
                to="#"
                onClick={() => paginate(currentPage + 1)}
              >
                <Icon icon="ep:d-arrow-right" className="" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default AssignRoleLayer;
